import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Flex, VStack, Image, Input, Fieldset, Card, Box } from '@chakra-ui/react';
import { PasswordInput } from '@/components/ui/password-input';
import { Field } from '@/components/ui/field';
import { Button } from '@/components/ui/button';
import { toaster } from '@/components/ui/toaster';
import { loginApi } from '@/api/auth';
import { useAuth } from '@/contexts/AuthContext';
import { LuArrowLeft } from 'react-icons/lu';

export type LoginFormValues = {
  accountId: string;
  password: string;
};

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  // ログイン処理中かどうか
  const [isLoad, setIsLoad] = React.useState<boolean>(false);

  // ログインエラーメッセージ
  const [errorMessage, setErrorMessage] = React.useState<string>();

  // ログインフォームの制御
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    defaultValues: {
      accountId: '',
      password: '',
    },
  });

  // ログイン処理
  const onSubmit: SubmitHandler<LoginFormValues> = async data => {
    setIsLoad(true);
    setErrorMessage(undefined);
    try {
      const authData = await loginApi(data);

      // トークンを直接localStorageに保存（確実にセットする）
      const expiresAt = Date.now() + authData.expiresIn;
      localStorage.setItem('authToken', authData.token);
      localStorage.setItem('tokenExpiresAt', expiresAt.toString());

      // AuthContextの状態を更新
      await login(authData);

      toaster.create({
        title: 'ログイン成功',
        description: 'ログインしました',
        type: 'success',
      });

      // 画面遷移前に少し待機してトークンが確実にセットされるようにする
      await new Promise(resolve => setTimeout(resolve, 100));

      navigate('/main');
    } catch (error) {
      console.error(error);
      const message =
        error instanceof Error
          ? error.message
          : 'ログインに失敗しました。時間をおいて再度お試しください。';
      setErrorMessage(message);
      toaster.create({
        title: 'ログインエラー',
        description: message,
        type: 'error',
      });
    }
    setIsLoad(false);
  };

  return (
    <Flex justify="center" flex={1} mt={20} px={2}>
      <VStack w="full" maxW="500px" alignItems="stretch">
        <Image src="/img/BunPay-logo-sample2.png" alt="main logo" w="20vh" alignSelf="center" />
        <Card.Root variant="elevated" w="full">
          <Card.Body w="full">
            <VStack gap={4} w="full" alignItems="stretch">
              <Box as="form" onSubmit={handleSubmit(onSubmit)} w="full">
                <Fieldset.Root invalid={!!errorMessage} w="full">
                  {/* <Stack>
                      <Fieldset.Legend>ログイン</Fieldset.Legend>
                    </Stack> */}
                  <Fieldset.Content w="full">
                    <Field
                      label="Account ID"
                      invalid={!!errors.accountId}
                      errorText={errors.accountId?.message}
                      w="full"
                    >
                      <Input
                        placeholder="bunpay"
                        focusRingColor="teal"
                        variant="subtle"
                        fontSize="16px"
                        {...register('accountId', { required: 'アカウントIDは必須です。' })}
                      />
                    </Field>
                    <Field
                      label="Password"
                      invalid={!!errors.password}
                      errorText={errors.password?.message}
                      w="full"
                    >
                      <PasswordInput
                        placeholder="password"
                        focusRingColor="teal"
                        variant="subtle"
                        fontSize="16px"
                        {...register('password', { required: 'パスワードは必須です。' })}
                      />
                    </Field>
                  </Fieldset.Content>
                  <Fieldset.ErrorText>{errorMessage}</Fieldset.ErrorText>
                  <Button
                    type="submit"
                    colorPalette="teal"
                    size="lg"
                    mt={8}
                    loading={isLoad}
                    w="full"
                  >
                    ログイン
                  </Button>
                </Fieldset.Root>
              </Box>
            </VStack>
          </Card.Body>
        </Card.Root>

        {/* 戻るボタン */}
        <Button variant="ghost" size="sm" alignSelf="center" mt={2} onClick={() => navigate('/')}>
          <LuArrowLeft />
          戻る
        </Button>
      </VStack>
    </Flex>
  );
};

export default LoginPage;
